import React, { forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationBlockerProps {
  hasUnsavedChanges: boolean;
  onConfirm: () => void;
  children?: React.ReactNode;
}

export interface NavigationBlockerRef {
  handleNavigation: (path: string) => void;
}

const NavigationBlocker = forwardRef<NavigationBlockerRef, NavigationBlockerProps>(
  ({ hasUnsavedChanges, onConfirm, children }, ref) => {
    const navigate = useNavigate();

    React.useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = '';
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleNavigation = (path: string) => {
      if (hasUnsavedChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
          onConfirm();
          navigate(path);
        }
      } else {
        navigate(path);
      }
    };

    useImperativeHandle(ref, () => ({
      handleNavigation
    }));

    return <>{children}</>;
  }
);

NavigationBlocker.displayName = 'NavigationBlocker';

export default NavigationBlocker;
